import { Injectable, OnDestroy } from '@angular/core';
import { HttpResponse } from '@angular/common/http';
import { Subscription } from 'rxjs/Subscription';
import * as moment from 'moment';
import { AdminService } from '../../services/admin.service';
import { UiLocaleService } from '../../services/ui-locale.service';
import { FullUser } from '../../models/full-user.model';
import { CurrencyCode, PAYROLL_CURRENCY_CODES, SalaryContext, PayrollMonthlyRate } from '../../models/payroll-currency.model';
import { PAYROLL_CALCULATION_METHOD, PayrollCalculationMethodSource, resolvePayrollCalculationMethod } from '../../models/deal-payroll.model';
import { SkillProgressApiService } from '../common/skill-learning/services/skill-progress-api.service';
import { SkillDepartmentLearningProgressResponse, SkillTrainingProgressResponse } from '../common/skill-learning/models/skill-learning.model';
import { KarmaContent } from '../my-working-report/karma-content.model';
import { collectProfileTrainings, profileKarmaTotal } from './my-profile-summary.presentation';
import { profileSalaryAmount } from '../views/view-user-profile/view-user-profile-payroll.presentation';
import {dealPayrollGradeKey} from '../../models/deal-payroll-presentation';

const PREVIEW_COUNT = 3;
const FIRST_INDEX = 0;
const MONTH_UNIT = 'month';
const DATE_FORMAT = 'YYYY-MM-DD';
const SALARY_LABEL = 'SALARY';
const MONTHLY_RATE_LABEL = 'PROFILE_MONTHLY_RATE';
const MONTH_KEYS = [
  'JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE',
  'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'
];

export interface ProfileCompletedTraining extends SkillTrainingProgressResponse { displayName: string; }

@Injectable()
export class MyProfileSummaryFacade implements OnDestroy {
  monthlyRate: PayrollMonthlyRate = null;
  salaryVisible = false;
  salaryLoaded = false;
  salaryError = false;
  salaryAmount: number = null;
  salaryCurrency: CurrencyCode = null;
  salaryLabelKey = SALARY_LABEL;
  deal = false;
  gradeSalary: number = null;
  gradeCurrency: CurrencyCode = null;
  gradeLabelKey: string = null;
  karmaLoaded = false;
  karmaError = false;
  karmaTotal: number = null;
  monthKey: string;
  monthYear: number;
  learningLoaded = false;
  learningError = false;
  trainings: ProfileCompletedTraining[] = [];
  learningExpanded = false;
  readonly previewCount = PREVIEW_COUNT;
  private requests = new Subscription();

  constructor(private admin: AdminService, private progress: SkillProgressApiService,
              private locale: UiLocaleService) {}

  load(user: FullUser & PayrollCalculationMethodSource, now = new Date()): void {
    this.requests.unsubscribe();
    this.requests = new Subscription();
    this.loadKarma(user.id, now);
    this.loadLearning();
    this.loadSalary(user);
  }

  ngOnDestroy(): void { this.requests.unsubscribe(); }

  toggleLearning(): void { this.learningExpanded = !this.learningExpanded; }

  get visibleTrainings(): ProfileCompletedTraining[] {
    return this.learningExpanded ? this.trainings : this.trainings.slice(FIRST_INDEX, PREVIEW_COUNT);
  }

  trainingName(training: SkillTrainingProgressResponse): string {
    return this.locale.resolveContent(training.specializationName, training.specializationNameTranslation);
  }

  refreshTrainingNames(): void {
    this.trainings.forEach(training => training.displayName = this.trainingName(training));
  }

  trackTraining(index: number, training: SkillTrainingProgressResponse): string { return training.specializationId; }

  private loadKarma(userId: string, now: Date): void {
    const month = moment(now);
    this.monthKey = MONTH_KEYS[month.month()];
    this.monthYear = month.year();
    this.karmaLoaded = false;
    this.karmaError = false;
    this.karmaTotal = null;
    const from = month.clone().startOf(MONTH_UNIT).format(DATE_FORMAT);
    const to = month.clone().endOf(MONTH_UNIT).format(DATE_FORMAT);
    this.requests.add(this.admin.getKarmaInfo(userId, from, to).subscribe(
      (response: HttpResponse<KarmaContent[]>) => {
        this.karmaTotal = profileKarmaTotal(response.body);
        this.karmaError = this.karmaTotal === null;
        this.karmaLoaded = true;
      }, () => { this.karmaLoaded = true; this.karmaError = true; }
    ));
  }

  private loadLearning(): void {
    this.learningLoaded = false;
    this.learningError = false;
    this.learningExpanded = false;
    this.trainings = [];
    this.requests.add(this.progress.fetchDepartmentLearningProgress().subscribe(
      (response: SkillDepartmentLearningProgressResponse) => {
        this.learningError = !response || !Array.isArray(response.departments);
        this.trainings = this.learningError ? [] : collectProfileTrainings(response.departments)
          .map(training => Object.assign({}, training, {displayName: this.trainingName(training)}));
        this.learningLoaded = true;
      }, () => { this.learningLoaded = true; this.learningError = true; }
    ));
  }

  private loadSalary(user: FullUser & PayrollCalculationMethodSource): void {
    this.monthlyRate = null;
    const method = resolvePayrollCalculationMethod(user);
    this.salaryVisible = true;
    this.deal = method === PAYROLL_CALCULATION_METHOD.PLANFIX_DEAL;
    this.gradeSalary = null; this.gradeCurrency = null; this.gradeLabelKey = null;
    this.salaryLoaded = false;
    this.salaryError = false;
    this.salaryAmount = null;
    this.salaryCurrency = null;
    this.salaryLabelKey = method === PAYROLL_CALCULATION_METHOD.PLANFIX_HOURLY ? MONTHLY_RATE_LABEL : SALARY_LABEL;
    if (!this.salaryVisible) { return; }
    this.requests.add(this.admin.getSalaryContext(user.id).subscribe(
      context => this.applySalary(context, user.id),
      () => { this.salaryLoaded = true; this.salaryError = true; }
    ));
  }

  private applySalary(context: SalaryContext, userId: string): void {
    this.salaryLoaded = true;
    this.salaryError = !context || context.userId !== userId;
    if (this.salaryError) { return; }
    this.monthlyRate = (this.deal ? context.gradeMonthlyRate : context.salaryMonthlyRate) || null;
    this.salaryCurrency = context.effectiveCurrency;
    this.salaryAmount = profileSalaryAmount(context.salary);
    this.gradeSalary = profileSalaryAmount(context.gradeMonthlySalary);
    this.gradeCurrency = context.payrollCurrency;
    this.gradeLabelKey = context.gradeCode ? dealPayrollGradeKey(context.gradeCode) : null;
    if (this.salaryAmount !== null && PAYROLL_CURRENCY_CODES.indexOf(this.salaryCurrency) < FIRST_INDEX) {
      this.salaryAmount = null;
      this.salaryError = true;
    }
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/my-profile/my-profile-summary.facade.ts